import React, { useEffect, useRef, useState, useCallback } from "react";
import { LogIn, LogOut, ScanFace, Delete, KeyRound, Loader2, CheckCircle2, XCircle, WifiOff } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { kioskApi, getKioskToken, setKioskToken } from "../lib/api";
import { loadModels, detectFace } from "../lib/face";

function KioskLogin({ onDone }) {
  const [f, setF] = useState({ org_code: "", username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const d = await kioskApi.login(f.org_code.trim(), f.username.trim(), f.password); setKioskToken(d.token); onDone(); }
    catch (err) { setError(err?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl p-8 shadow-2xl">
        <div className="flex items-center gap-2 justify-center mb-2"><LogoMark size={36} /><span className="font-display font-extrabold text-2xl"><span className="text-brand-navy">HR</span><span className="text-brand-sky">Flo</span></span></div>
        <div className="text-center text-xs font-bold tracking-[0.25em] text-brand-blue mb-6">TIME · KIOSK</div>
        <h1 className="font-display font-bold text-lg text-brand-navy text-center">Kiosk sign in</h1>
        <p className="text-slate-500 text-sm text-center mt-1 mb-6">Authenticate this device to start.</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Organisation code</Label><Input value={f.org_code} onChange={(e) => setF({ ...f, org_code: e.target.value })} placeholder="e.g. kgalagadi" required /></div>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Kiosk username</Label><Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} placeholder="e.g. kiosk-main" required /></div>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required /></div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-xl h-12 text-base"><LogIn className="w-4 h-4 mr-1" />{loading ? "Signing in..." : "Enter kiosk mode"}</Button>
        </form>
      </div>
    </div>
  );
}

export default function Kiosk() {
  const [authed, setAuthed] = useState(!!getKioskToken());
  const [info, setInfo] = useState(null);
  const [modelsReady, setModelsReady] = useState(false);
  const [scanning, setScanning] = useState(true);
  const [status, setStatus] = useState({ type: "scan" }); // scan | busy | success | fail
  const [mode, setMode] = useState("face"); // face | pin
  const [pin, setPin] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const busyRef = useRef(false);
  const cooldownRef = useRef(0);

  const loadInfo = useCallback(async () => {
    try { setInfo(await kioskApi.me()); } catch (e) { setKioskToken(null); setAuthed(false); }
  }, []);

  useEffect(() => { if (authed) loadInfo(); }, [authed, loadInfo]);

  // start camera + models
  useEffect(() => {
    if (!authed) return;
    let active = true;
    (async () => {
      try {
        await loadModels();
        if (active) setModelsReady(true);
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640, height: 480 } });
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      } catch (e) { /* camera denied */ }
    })();
    return () => { active = false; if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, [authed]);

  const showResult = (type, payload) => {
    setStatus({ type, ...payload });
    setScanning(false);
    setTimeout(() => { setStatus({ type: "scan" }); setScanning(true); setMode("face"); setPin(""); }, type === "success" ? 3200 : 2600);
  };

  // face scan loop
  useEffect(() => {
    if (!authed || !modelsReady || mode !== "face") return;
    let stop = false;
    const tick = async () => {
      if (stop) return;
      if (!scanning || busyRef.current || Date.now() < cooldownRef.current) { return schedule(); }
      const v = videoRef.current;
      if (v && v.readyState >= 2) {
        const r = await detectFace(v);
        if (r.ok && !busyRef.current) {
          busyRef.current = true;
          setStatus({ type: "busy" });
          try {
            const res = await kioskApi.identify(r.descriptor);
            showResult("success", res);
            cooldownRef.current = Date.now() + 4000;
          } catch (err) {
            const code = err?.response?.status;
            if (code === 429) { cooldownRef.current = Date.now() + 3000; setStatus({ type: "scan" }); }
            else { showResult("fail", { reason: err?.response?.data?.detail }); }
          } finally { busyRef.current = false; }
        }
      }
      schedule();
    };
    const schedule = () => { if (!stop) setTimeout(tick, 700); };
    schedule();
    return () => { stop = true; };
  }, [authed, modelsReady, mode, scanning]);

  const submitPin = async () => {
    if (pin.length < 4) return;
    setStatus({ type: "busy" });
    try { const res = await kioskApi.pin(pin); showResult("success", res); }
    catch (err) { showResult("fail", { reason: err?.response?.data?.detail || "Invalid PIN" }); }
  };

  const signOut = () => { setKioskToken(null); setAuthed(false); if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };

  if (!authed) return <KioskLogin onDone={() => setAuthed(true)} />;

  const s = status;
  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      <header className="flex items-center justify-between px-6 sm:px-10 py-5">
        <div className="flex items-center gap-2"><LogoMark size={32} /><span className="font-display font-extrabold text-xl"><span className="text-white">HR</span><span className="text-brand-sky">Flo</span></span><span className="ml-2 text-xs font-bold tracking-[0.2em] text-brand-sky">TIME</span></div>
        <div className="text-right">
          <div className="text-sm font-semibold">{info?.device?.name}</div>
          <div className="text-xs text-slate-400">{info?.organization_name} · {info?.device?.location}</div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 pb-8">
        <div className="relative w-full max-w-lg aspect-[4/3] rounded-3xl overflow-hidden bg-slate-900 ring-1 ring-white/10 shadow-2xl">
          <video ref={videoRef} muted playsInline className="w-full h-full object-cover scale-x-[-1]" />
          {/* overlay states */}
          {s.type === "scan" && mode === "face" && (
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 pointer-events-none">
              <div className="absolute inset-6 border-2 border-brand-sky/50 rounded-3xl" />
              <div className="bg-black/50 backdrop-blur px-5 py-2 rounded-full text-sm font-medium flex items-center gap-2"><ScanFace className="w-4 h-4 text-brand-sky" /> {modelsReady ? "Look at the camera" : "Preparing camera..."}</div>
            </div>
          )}
          {s.type === "busy" && <div className="absolute inset-0 bg-slate-950/70 flex flex-col items-center justify-center"><Loader2 className="w-12 h-12 animate-spin text-brand-sky" /><div className="mt-3 text-sm text-slate-300">Verifying…</div></div>}
          {s.type === "success" && (
            <div className="absolute inset-0 bg-emerald-600/95 flex flex-col items-center justify-center text-center px-6">
              <CheckCircle2 className="w-20 h-20 mb-3" />
              <div className="text-lg opacity-90">{s.event_type === "CHECK_IN" ? "Good day," : "Goodbye,"}</div>
              <div className="font-display font-extrabold text-3xl">{s.employee_name}</div>
              <div className="mt-2 text-xl font-semibold">{s.event_type === "CHECK_IN" ? "Check-in successful" : "Check-out successful"} · {s.time}</div>
              {s.event_type === "CHECK_OUT" && s.hours != null && <div className="mt-1 text-sm opacity-90">Hours today: {s.hours}</div>}
              {s.note && <div className="mt-2 text-xs bg-white/20 px-3 py-1 rounded-full">{s.note}</div>}
            </div>
          )}
          {s.type === "fail" && (
            <div className="absolute inset-0 bg-slate-900/95 flex flex-col items-center justify-center text-center px-6">
              <XCircle className="w-16 h-16 mb-3 text-red-400" />
              <div className="font-display font-bold text-2xl">We couldn't recognise you</div>
              <div className="mt-2 text-slate-300">Please try again or use your PIN.</div>
            </div>
          )}
        </div>

        {/* PIN pad */}
        {mode === "pin" && s.type !== "success" && (
          <div className="mt-6 w-full max-w-xs">
            <div className="text-center text-sm text-slate-300 mb-2">Enter your employee PIN</div>
            <div className="flex justify-center gap-2 mb-4">
              {[0, 1, 2, 3].map((i) => <div key={i} className={`w-4 h-4 rounded-full ${i < pin.length ? "bg-brand-sky" : "bg-white/20"}`} />)}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {["1","2","3","4","5","6","7","8","9"].map((n) => (
                <button key={n} onClick={() => setPin((p) => (p + n).slice(0, 8))} className="h-14 rounded-2xl bg-white/10 hover:bg-white/20 text-2xl font-semibold transition-colors">{n}</button>
              ))}
              <button onClick={() => setPin("")} className="h-14 rounded-2xl bg-white/5 hover:bg-white/10 text-sm">Clear</button>
              <button onClick={() => setPin((p) => (p + "0").slice(0, 8))} className="h-14 rounded-2xl bg-white/10 hover:bg-white/20 text-2xl font-semibold">0</button>
              <button onClick={() => setPin((p) => p.slice(0, -1))} className="h-14 rounded-2xl bg-white/5 hover:bg-white/10 flex items-center justify-center"><Delete className="w-5 h-5" /></button>
            </div>
            <Button onClick={submitPin} disabled={pin.length < 4} className="mt-4 w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-xl h-12">Confirm</Button>
          </div>
        )}

        {/* controls */}
        {s.type !== "success" && s.type !== "busy" && (
          <div className="mt-6 flex items-center gap-3">
            {mode === "face" ? (
              <button onClick={() => setMode("pin")} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-xl px-5 h-11 text-sm font-medium transition-colors"><KeyRound className="w-4 h-4" /> Use PIN instead</button>
            ) : (
              <button onClick={() => { setMode("face"); setPin(""); }} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-xl px-5 h-11 text-sm font-medium transition-colors"><ScanFace className="w-4 h-4" /> Use face instead</button>
            )}
          </div>
        )}
      </main>

      <footer className="px-6 sm:px-10 py-4 flex items-center justify-between text-xs text-slate-500">
        <span>{info?.enrolled_count ?? 0} employees enrolled · Basic liveness (REQUIRES SPECIALIST PROVIDER for production)</span>
        <button onClick={signOut} className="flex items-center gap-1.5 hover:text-white"><LogOut className="w-3.5 h-3.5" /> Exit kiosk</button>
      </footer>
    </div>
  );
}
